import "../../css/GlobalStyles.css";
import { memo, useCallback, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  getAccountState,
  getIncompleteOnboardingFlows,
} from "../../redux/slices/accountSlice";
import { MixpanelContext } from "../../context/AnalyticsService";
import { Colors } from "../../utils/colors";
import { useNavigate } from "../../hooks/useNavigate";
import { Icon } from "@iconify/react";
import RectangleButton from "../../components/Buttons/RectangleButton";
import StandardBorderedContainer from "../../components/Containers/StandardBorderedContainer";
import {
  NavigationId,
  NewProductNavigationId,
} from "../../navigation/AppParamList";
import { onCreatorPanelNavigatePath } from "../../utils/navigationUtils";
import FullScreenContainer from "../../components/Containers/FullScreenContainer";
import { getShareDomain } from "../../utils/shareLink";
import { getProductDetails } from "../../utils/productUtils";
import { ProductType } from "@markit/common.utils";
import { EventType, OnboardingFlowType } from "@markit/common.types";
import CreateFormAndTrackingLinkModal from "../../components/CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import OnboardingTourStep from "../../components/Onboarding/OnboardingTourStep";
import {
  NEWSLETTER_BLOG_ONBOARDING_STEPS,
  ORGANIZE_EVENTS_ONBOARDING_STEPS,
  SELL_PRODUCTS_ONBOARDING_STEPS,
} from "../../utils/onboardingFlowUtils";

// When multiple of these flows are incomplete at once, only the highest
// priority one's step 1 tooltip shows on this screen - newsletter first,
// then sell products, then events.
const SELECT_PRODUCT_TOUR_PRIORITY = [
  OnboardingFlowType.NEWSLETTER_BLOG,
  OnboardingFlowType.SELL_PRODUCTS,
  OnboardingFlowType.ORGANIZE_EVENTS,
];

// Maps a priority flow type to the item that should be pre-selected on this
// screen when that flow's tour is active, following the same priority order.
const getDefaultProductType = (
  flowType: OnboardingFlowType | undefined
): ProductType => {
  switch (flowType) {
    case OnboardingFlowType.NEWSLETTER_BLOG:
      return ProductType.TEXT_POST;
    case OnboardingFlowType.SELL_PRODUCTS:
      return ProductType.CUSTOM_PRODUCT;
    case OnboardingFlowType.ORGANIZE_EVENTS:
      return ProductType.EVENT;
    default:
      return ProductType.TEXT_POST;
  }
};

const SelectProductDashboard = () => {
  const { accountData } = useSelector(getAccountState).account;
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const topPriorityFlowType = SELECT_PRODUCT_TOUR_PRIORITY.find((type) =>
    incompleteOnboardingFlows.some((flow) => flow.id === type)
  );
  const [productSelected, setProductSelected] = useState<ProductType>(() =>
    getDefaultProductType(topPriorityFlowType)
  );
  const [createFormVisible, setCreateFormVisible] = useState(false);
  // Once the user opens the Forms modal, the (unrelated) product tour step is done for
  // this visit - it shouldn't come back just because the modal was closed again.
  const [tourAbandoned, setTourAbandoned] = useState(false);

  const topPriorityTour = useMemo(() => {
    const flowType = topPriorityFlowType;
    switch (flowType) {
      case OnboardingFlowType.NEWSLETTER_BLOG:
        return {
          flowType,
          totalSteps: NEWSLETTER_BLOG_ONBOARDING_STEPS,
          specificContent:
            "Try creating a newsletter-style post to share long-form content and updates with your subscribers.",
        };
      case OnboardingFlowType.SELL_PRODUCTS:
        return {
          flowType,
          totalSteps: SELL_PRODUCTS_ONBOARDING_STEPS,
          specificContent:
            "Select a digital product here (video, audio, PDF, or custom product) to start selling directly through Markit.",
        };
      case OnboardingFlowType.ORGANIZE_EVENTS:
        return {
          flowType,
          totalSteps: ORGANIZE_EVENTS_ONBOARDING_STEPS,
          specificContent:
            "Try creating an event to sell tickets, collect RSVPs, and send text reminders leading up to the event.",
        };
      default:
        return undefined;
    }
  }, [topPriorityFlowType]);

  const styles = {
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: 20,
    },
  };

  const eventOnPress = useCallback(() => {
    mixpanel.track("Pressed Create An Event", {
      pathname: location.pathname,
      button_location: "select product",
      platform: "webapp",
    });
    window.location.href = `${getShareDomain()}${NewProductNavigationId.EVENT}`;
  }, [mixpanel]);

  const linkAnEventOnPress = useCallback(() => {
    mixpanel.track("Pressed Create External Event Link", {
      distinct_id: accountData.uid,
      platform: "webapp",
      type: "event_link",
      button_location: "select product",
    });
    navigate(NewProductNavigationId.LINK_EVENT);
  }, [accountData.uid, mixpanel, navigate]);

  const textPostOnPress = useCallback(() => {
    mixpanel.track("Pressed Create New Product Type", {
      distinct_id: accountData.uid,
      platform: "webapp",
      product_type: "post",
    });
    navigate(NewProductNavigationId.POST);
  }, [accountData.uid, mixpanel, navigate]);

  const webinarOnPress = useCallback(() => {
    mixpanel.track("Pressed Create New Product Type", {
      distinct_id: accountData.uid,
      platform: "webapp",
      product_type: "webinar",
    });
    window.location.href = `${getShareDomain()}${
      NewProductNavigationId.WEBINAR
    }`;
  }, [mixpanel, accountData.uid]);

  const formOnPress = useCallback(() => {
    mixpanel.track("Pressed Create New Product Type", {
      distinct_id: accountData.uid,
      platform: "webapp",
      product_type: "form",
    });
    setCreateFormVisible(true);
    setTourAbandoned(true);
  }, [accountData.uid, mixpanel]);

  const specificProductOnPress = useCallback(
    (type: ProductType) => {
      mixpanel.track("Pressed Create New Product Type", {
        distinct_id: accountData.uid,
        platform: "webapp",
        product_type: type,
      });
      const formattedType = type.toLowerCase().replace(/\s/g, "-");
      navigate(`/new-product/${formattedType}`);
    },
    [accountData.uid, mixpanel, navigate]
  );

  const productOnPress = useCallback(() => {
    switch (productSelected) {
      case ProductType.EVENT:
        eventOnPress();
        break;
      case ProductType.LINK_AN_EVENT:
        linkAnEventOnPress();
        break;
      case ProductType.TEXT_POST:
        textPostOnPress();
        break;
      case ProductType.FORM:
        formOnPress();
        break;
      case ProductType.VIDEO:
      case ProductType.AUDIO:
      case ProductType.CUSTOM_PRODUCT:
      case ProductType.PDF:
        specificProductOnPress(productSelected);
        break;
      case ProductType.WEBINAR:
        webinarOnPress();
        break;
      case ProductType.MEMBERSHIP:
        onCreatorPanelNavigatePath(navigate, NavigationId.HOME_MEMBERSHIPS, 2);
        break;
    }
  }, [
    eventOnPress,
    formOnPress,
    linkAnEventOnPress,
    navigate,
    productSelected,
    specificProductOnPress,
    textPostOnPress,
    webinarOnPress,
  ]);

  const renderProductItem = useCallback(
    (
      product: ProductType,
      description: string,
      icon: string,
      iconColor: string,
      disabled?: boolean
    ) => (
      <StandardBorderedContainer
        containerStyles={{
          padding: 14,
          borderRadius: 20,
          backgroundColor: Colors.WHITE1,
          opacity: disabled ? 0.4 : 1,
          pointerEvents: disabled ? "none" : "auto",
          cursor: "pointer",
          ...(productSelected === product
            ? {
                border: `2px solid ${Colors.BLACK}`,
              }
            : {}),
        }}
        onPress={() => setProductSelected(product)}
      >
        <div className="AlignedRow" style={{ gap: 14 }}>
          <div
            className="Centering"
            style={{
              borderRadius: 16,
              padding: 14,
              backgroundColor: Colors.WHITE,
              border: `0.5px solid ${Colors.GRAY2}`,
            }}
          >
            <Icon icon={icon} height={40} style={{ color: iconColor }} />
          </div>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="sectionTitle">{product}</span>
            <span className="smallBodySubtext">{description}</span>
          </div>
        </div>
      </StandardBorderedContainer>
    ),
    [productSelected]
  );

  return (
    <FullScreenContainer
      title="Select Type"
      helmetTitle="Select Product Type"
      backOnPress={() => navigate(NavigationId.HOME_PRODUCTS)}
      rightHeaderComp={
        <div>
          <RectangleButton
            buttonLabel="Continue"
            iconRight={
              <Icon
                icon="ion:arrow-forward"
                height={16}
                style={{ color: Colors.WHITE }}
              />
            }
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            onPress={productOnPress}
            disabled={!productSelected}
            containerStyles={{ padding: 14 }}
          />
        </div>
      }
    >
      <div className="ColumnNormal" style={{ gap: 30 }}>
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <span className="bodyMedium">Products</span>
          <div style={styles.gridContainer}>
            {renderProductItem(
              ProductType.TEXT_POST,
              "Create newsletter-style posts with more room to share long-form content and visuals that won't fit in a text.",
              getProductDetails(EventType.POST).icon,
              getProductDetails(EventType.POST).color
            )}
            {renderProductItem(
              ProductType.FORM,
              "Create a form to collect contact info, signups, or responses from your audience.",
              getProductDetails(EventType.GENERIC_LINK).icon,
              getProductDetails(EventType.GENERIC_LINK).color
            )}
            {renderProductItem(
              ProductType.VIDEO,
              "Host your own high-quality video content or tutorials directly within Markit.",
              getProductDetails(EventType.VIDEO).icon,
              getProductDetails(EventType.VIDEO).color
            )}
            {renderProductItem(
              ProductType.AUDIO,
              "Share your voice through podcasts, music, or voice memos hosted directly on Markit.",
              getProductDetails(EventType.AUDIO).icon,
              getProductDetails(EventType.AUDIO).color
            )}
            {renderProductItem(
              ProductType.PDF,
              "Upload and distribute professional documents, ebooks, or guides for easy viewing and download.",
              getProductDetails(EventType.PDF).icon,
              getProductDetails(EventType.PDF).color
            )}
            {renderProductItem(
              ProductType.CUSTOM_PRODUCT,
              "Feature any product. Upload images and add details to start selling exactly what you want.",
              getProductDetails(EventType.CUSTOM_PRODUCT).icon,
              getProductDetails(EventType.CUSTOM_PRODUCT).color
            )}
          </div>
        </div>
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <span className="bodyMedium">Events</span>
          <div style={styles.gridContainer}>
            {renderProductItem(
              ProductType.EVENT,
              "Markit has a full fledged ticketing system. Run events, sell tickets, manage event automations and more.",
              getProductDetails(EventType.MARKIT).icon,
              getProductDetails(EventType.MARKIT).color
            )}
            {renderProductItem(
              ProductType.WEBINAR,
              "Host your webinar on Markit.",
              getProductDetails(EventType.WEBINAR).icon,
              getProductDetails(EventType.WEBINAR).color
            )}
            {renderProductItem(
              ProductType.LINK_AN_EVENT,
              "Create a unique link to track analytics and redirect users to your linked event.",
              getProductDetails(EventType.EXTERNAL_DATA).icon,
              getProductDetails(EventType.EXTERNAL_DATA).color
            )}
          </div>
        </div>
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <span className="bodyMedium">Services</span>
          <div style={styles.gridContainer}>
            {renderProductItem(
              ProductType.MEMBERSHIP,
              "Make money from texting. Charge monthly and annual fees from subscribers.",
              "ion:ribbon",
              Colors.GREEN2
            )}
            {renderProductItem(
              ProductType.CONSULTING_SESSION,
              "Coming soon...",
              "ion:rocket-sharp",
              Colors.ORANGE1,
              true
            )}
            {renderProductItem(
              ProductType.COURSE_OR_TUTORIAL,
              "Coming soon...",
              "ion:briefcase",
              Colors.PURPLE8,
              true
            )}
          </div>
        </div>
      </div>
      {topPriorityTour && !tourAbandoned ? (
        <OnboardingTourStep
          key={`select-product-tour-${topPriorityTour.flowType}-step-1`}
          flowType={topPriorityTour.flowType}
          target="body"
          content={topPriorityTour.specificContent}
          stepNumber={1}
          totalSteps={topPriorityTour.totalSteps}
          placement="center"
        />
      ) : null}
      {createFormVisible ? (
        <CreateFormAndTrackingLinkModal
          isVisible={createFormVisible}
          setIsVisible={setCreateFormVisible}
          eventType={EventType.GENERIC_LINK}
        />
      ) : null}
    </FullScreenContainer>
  );
};

export default memo(SelectProductDashboard);
